import {
  AI_FEATURE_LABELS,
  type AiUsageReport,
  type AppSettings,
  describeUsageScope,
  formatUsd,
} from '@acasa/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Card } from '@/components/card'
import { useSession } from '@/features/auth/use-auth'
import { ApiRequestError } from '@/lib/api'
import { AiUsageChart } from './ai-usage-chart'
import { aiUsageCsvHref, aiUsageKey, useAiUsage } from './use-ai-usage'
import { useAppSettings, useUpdateAppSettings } from './use-app-settings'

/**
 * KI-Nutzung — what the AI features billed, month by month, and the limit.
 *
 * Admin only: what the agency is charged is an admin's business. Every figure
 * here is the BILLED one, markup included, because that is the only one the
 * server sends; the purchase price and the margin are not hidden on this page,
 * they are absent from the response it is built from.
 *
 * The limit is edited here, in the agency's dollars, and saved through the
 * ordinary settings PATCH — but this card edits ONLY the `aiUsage` group and
 * keys its draft on that group alone, so a save in another card cannot reset a
 * half-typed amount here, and this one cannot reach into theirs.
 */

/** At this share of the limit the bar turns amber — early enough to act on. */
const WARN_AT = 0.8

type AiUsageGroup = AppSettings['aiUsage']

export function AiUsagePanel() {
  const { user } = useSession()
  const isAdmin = user?.role === 'admin'

  const [month, setMonth] = useState<string | null>(null)
  const usage = useAiUsage(month, isAdmin)
  const settings = useAppSettings(isAdmin)
  const save = useUpdateAppSettings()
  const queryClient = useQueryClient()

  const limitFieldId = useId()
  const anchor = useRef<HTMLDivElement>(null)
  const location = useLocation()

  // Structural sharing keeps the GROUP referentially stable unless its value
  // changes, so this fires on a save of this card and on nothing else.
  const group: AiUsageGroup | undefined = settings.data?.settings.aiUsage
  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    setDraft(group ? toInput(group.monthlyLimitMicroUsd) : '')
  }, [group])

  // The Bild-KI page links here; the router does not scroll to a hash itself.
  const report = usage.data
  useEffect(() => {
    if (location.hash === '#ki-nutzung' && report) {
      anchor.current?.scrollIntoView({ block: 'start' })
    }
  }, [location.hash, report])

  if (!isAdmin) return null

  const parsed = parseLimit(draft)
  const dirty = group !== undefined && parsed.ok && parsed.value !== group.monthlyLimitMicroUsd
  const busy = save.isPending

  const submit = () => {
    if (!parsed.ok) return
    setSaved(false)
    save.mutate(
      { aiUsage: { monthlyLimitMicroUsd: parsed.value } },
      {
        onSuccess: () => {
          setSaved(true)
          // The report carries the limit it is drawn against.
          void queryClient.invalidateQueries({ queryKey: aiUsageKey })
        },
      },
    )
  }

  const current = report ? monthIn(new Date(), report.timeZone) : null
  const shown = report?.month ?? null

  return (
    <div ref={anchor} id="ki-nutzung" className="scroll-mt-6">
      <Card
        title="KI-Nutzung"
        footer="Beträge in US-Dollar, wie sie in Rechnung gestellt werden. Fehlgeschlagene Aufrufe, die den KI-Dienst erreicht haben, werden mitgezählt — die Eingabe wurde verarbeitet."
      >
        {/* ------------------------------------------------------ header --- */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="border-line inline-flex items-stretch border">
            <button
              type="button"
              disabled={!shown}
              onClick={() => shown && setMonth(shiftMonth(shown, -1))}
              aria-label="Vorheriger Monat"
              className="text-muted hover:text-ink hover:bg-canvas px-2.5 text-sm transition-colors disabled:opacity-40"
            >
              ‹
            </button>
            <span className="border-line text-ink min-w-36 border-x px-3 py-1.5 text-center text-xs font-medium">
              {shown ? monthName(shown) : '…'}
            </span>
            <button
              type="button"
              disabled={!shown || !current || shown >= current}
              onClick={() => shown && setMonth(shiftMonth(shown, 1))}
              aria-label="Nächster Monat"
              className="text-muted hover:text-ink hover:bg-canvas px-2.5 text-sm transition-colors disabled:opacity-40"
            >
              ›
            </button>
          </div>

          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <label htmlFor={limitFieldId} className="flex flex-col gap-1">
              <span className="text-faint text-[10px] tracking-wider uppercase">
                Monatslimit (USD)
              </span>
              <input
                id={limitFieldId}
                value={draft}
                inputMode="decimal"
                autoComplete="off"
                placeholder="kein Limit"
                disabled={busy || group === undefined}
                aria-invalid={parsed.ok ? undefined : true}
                onChange={(event) => {
                  setDraft(event.target.value)
                  setSaved(false)
                }}
                className="border-line focus:border-brand bg-surface text-ink placeholder:text-faint tnum w-32 border px-2.5 py-1.5 text-right font-mono text-xs outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={!dirty || busy}
              className="bg-brand text-surface hover:bg-brand-strong px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {busy ? 'wird gespeichert …' : 'Speichern'}
            </button>
            {saved && !dirty && <span className="text-positive pb-2 text-xs">Gespeichert.</span>}
          </form>
        </div>

        {!parsed.ok && (
          <p className="text-danger mt-2 text-right text-xs">
            Betrag in Dollar, größer als null — oder leer für kein Limit.
          </p>
        )}
        {save.isError && (
          <p className="text-danger mt-2 text-right text-xs">{errorMessage(save.error)}</p>
        )}

        {usage.isPending && <p className="text-muted mt-5 text-sm">Wird geladen …</p>}
        {usage.isError && (
          <p className="text-danger mt-5 text-sm">
            KI-Nutzung konnte nicht geladen werden: {errorMessage(usage.error)}
          </p>
        )}

        {report && <ReportBody report={report} isCurrent={shown === current} />}
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------------ */

function ReportBody({ report, isCurrent }: { report: AiUsageReport; isCurrent: boolean }) {
  const spent = report.totals.billedMicroUsd
  const limit = report.limitMicroUsd
  const share = limit ? spent / limit : 0
  const tone = !limit
    ? 'bg-brand'
    : share >= 1
      ? 'bg-danger'
      : share >= WARN_AT
        ? 'bg-warning'
        : 'bg-brand'

  const when = new Intl.DateTimeFormat('de-DE', {
    timeZone: report.timeZone,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div className="mt-5 flex flex-col gap-5">
      {/* ------------------------------------------------ spent vs limit --- */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-display text-ink tnum text-[28px] leading-none">
            {formatUsd(spent)}
          </span>
          <span className="text-muted text-xs">
            {limit
              ? `von ${formatUsd(limit)} · ${Math.round(share * 100)} %`
              : isCurrent
                ? 'kein Monatslimit gesetzt'
                : 'abgeschlossener Monat — das Limit gilt für den laufenden'}
          </span>
        </div>

        {limit !== null && (
          <div
            className="bg-canvas border-line mt-2 h-2 overflow-hidden border"
            role="progressbar"
            aria-label="Verbrauch des Monatslimits"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.min(100, Math.round(share * 100))}
          >
            <div className={`${tone} h-full`} style={{ width: `${Math.min(100, share * 100)}%` }} />
          </div>
        )}

        {limit !== null && isCurrent && share >= 1 && (
          <p className="text-danger mt-2 text-xs">
            Monatslimit erreicht. Neue Aufträge werden abgelehnt, bis das Limit angehoben wird oder
            der Monat wechselt.
          </p>
        )}
        {limit !== null && isCurrent && share >= WARN_AT && share < 1 && (
          <p className="text-warning mt-2 text-xs">
            Mehr als {Math.round(WARN_AT * 100)} % des Monatslimits sind verbraucht.
          </p>
        )}
        {limit !== null && (
          <p className="text-faint mt-2 text-xs">
            Das Limit wird geprüft, wenn ein Auftrag startet, anhand einer Schätzung. Laufende
            Aufträge werden zu Ende geführt — der letzte Auftrag eines Monats kann das Limit deshalb
            geringfügig überschreiten.
          </p>
        )}
      </div>

      {/* ------------------------------------------------------- tiles --- */}
      <div className="grid gap-3 sm:grid-cols-2">
        {report.byFeature.map((entry) => (
          <figure key={entry.feature} className="border-line border p-4">
            <figcaption className="text-faint mb-2 text-[10px] font-medium tracking-[0.14em] uppercase">
              {AI_FEATURE_LABELS[entry.feature]}
            </figcaption>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-ink tnum font-mono text-sm">
                {formatUsd(entry.billedMicroUsd)}
              </span>
              <span className="text-muted text-xs">
                {entry.calls} {entry.calls === 1 ? 'Aufruf' : 'Aufrufe'}
              </span>
            </div>
          </figure>
        ))}
      </div>

      {/* ------------------------------------------------------- chart --- */}
      <figure>
        <figcaption className="text-faint mb-3 text-[10px] font-medium tracking-[0.14em] uppercase">
          Täglich · {report.timeZone}
        </figcaption>
        <AiUsageChart series={report.series} />
      </figure>

      {/* ------------------------------------------------------- table --- */}
      <figure>
        <figcaption className="mb-3 flex items-center justify-between gap-3">
          <span className="text-faint text-[10px] font-medium tracking-[0.14em] uppercase">
            Aufrufe
          </span>
          <a
            href={aiUsageCsvHref(report.month)}
            className="border-line hover:border-line-strong text-muted hover:text-ink border px-3 py-1.5 text-[11px] transition-colors"
          >
            CSV exportieren
          </a>
        </figcaption>

        {/* The table IS the accessible view of the chart, and the thing an
            invoice is checked against. */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-xs">
            <thead>
              <tr className="text-faint border-line border-b text-left text-[11px]">
                <th className="pb-2 font-medium">Zeit</th>
                <th className="pb-2 font-medium">Funktion</th>
                <th className="pb-2 font-medium">Objekt</th>
                <th className="pb-2 font-medium">Nutzer</th>
                <th className="pb-2 font-medium">Umfang</th>
                <th className="pb-2 text-right font-medium">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row) => (
                <tr key={row.id} className="border-line border-b align-top last:border-0">
                  <td className="text-muted tnum py-2 pr-3 font-mono whitespace-nowrap">
                    {when.format(new Date(row.at))}
                  </td>
                  <td className="text-ink py-2 pr-3 whitespace-nowrap">
                    {AI_FEATURE_LABELS[row.feature]}
                  </td>
                  <td className="py-2 pr-3">
                    {row.estate ? (
                      <>
                        <span className="text-ink font-mono">
                          {row.estate.externalNumber ?? `#${row.estate.id}`}
                        </span>
                        {row.estate.title && (
                          <span className="text-muted block">{truncate(row.estate.title)}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-faint">—</span>
                    )}
                  </td>
                  <td className="text-muted py-2 pr-3">{row.user?.name ?? '—'}</td>
                  <td className="text-muted py-2 pr-3">
                    {describeUsageScope(row)}
                    {row.status === 'failed' && (
                      <span className="text-danger block text-[11px]">fehlgeschlagen</span>
                    )}
                  </td>
                  <td className="text-ink tnum py-2 text-right font-mono whitespace-nowrap">
                    {formatUsd(row.billedMicroUsd)}
                  </td>
                </tr>
              ))}
              {report.rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted py-3 text-center">
                    Keine Aufrufe in diesem Monat.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </figure>
    </div>
  )
}

/* ------------------------------------------------------------------------ */

/** Micro-dollars into the field: "50,00", or empty for "no limit". */
function toInput(microUsd: number | null): string {
  return microUsd === null ? '' : formatUsd(microUsd, 2, { symbol: false })
}

/**
 * The field back into micro-dollars. Accepts what people type in Germany —
 * "50", "50,5", "1.000,00" — and "50.50" too. Empty means no limit.
 */
function parseLimit(text: string): { ok: true; value: number | null } | { ok: false } {
  const compact = text.replace(/[\s$]/g, '')
  if (compact === '') return { ok: true, value: null }

  // With a comma, dots are thousands separators. Without one, "1.000" is a
  // thousand too — read as one dollar it would set a limit nobody meant.
  const normalised = compact.includes(',')
    ? compact.replace(/\./g, '').replace(',', '.')
    : /^\d{1,3}(\.\d{3})+$/.test(compact)
      ? compact.replace(/\./g, '')
      : compact
  if (!/^\d+(\.\d{1,6})?$/.test(normalised)) return { ok: false }

  const value = Math.round(Number(normalised) * 1_000_000)
  return value > 0 ? { ok: true, value } : { ok: false }
}

/** `2026-10` → `2026-09` for -1. */
function shiftMonth(month: string, delta: number): string {
  const [year, value] = month.split('-').map(Number)
  const index = (year as number) * 12 + (value as number) - 1 + delta
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`
}

/** The month an instant falls in, in the report's own timezone. */
function monthIn(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' })
    .format(at)
    .slice(0, 7)
}

/** `2026-10` → `Oktober 2026`. */
function monthName(month: string): string {
  const [year, value] = month.split('-').map(Number)
  return new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year as number, (value as number) - 1, 15, 12)))
}

function truncate(text: string, max = 36): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return 'Unbekannter Fehler'
}
